import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { FileUploadItem } from '~/components/ui/uploaderQueue/FileUploadItem';
import {
  clearQueueAtom,
  fileUploadCompleteAtom,
  FileUploadCompleteEvt,
  fileUploadFnAtom,
  queueAddFilesAtom,
  queueFileAtomsList,
  queueSetFileProgress,
  queueStartUploadsAtom,
  queueStatusAtom,
} from '~/components/ui/uploaderQueue/useQueue';

export type UploaderQueueProps = {
  fileUploadFn: (id: string, file: File) => void;
  // onProgress: (id: string, progress: number) => void;
};
export type UploaderQueueRef = {
  setProgress: (progressEvt: { id: string; progress: number }) => void;
  setComplete: (evt: FileUploadCompleteEvt) => void;
};
export const UploaderQueue = forwardRef<UploaderQueueRef, UploaderQueueProps>(
  ({ fileUploadFn }: UploaderQueueProps, ref) => {
    const addFiles = useSetAtom(queueAddFilesAtom);
    const setUploadFn = useSetAtom(fileUploadFnAtom);
    const setFileUploadComplete = useSetAtom(fileUploadCompleteAtom);
    const fileAtomsList = useAtomValue(queueFileAtomsList);
    const queueStatus = useAtomValue(queueStatusAtom);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const startUploads = useSetAtom(queueStartUploadsAtom);
    const clearQueue = useSetAtom(clearQueueAtom);
    const setFileProgress = useSetAtom(queueSetFileProgress);
    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        setErrorMessage(null);
        addFiles(acceptedFiles);
      },
      [addFiles],
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
    });

    useEffect(() => {
      setUploadFn({ fileUploadFn });
    }, [fileUploadFn, setUploadFn]);

    useImperativeHandle(
      ref,
      () => ({
        setProgress: setFileProgress,
        setComplete: (evt: FileUploadCompleteEvt) => {
          if (evt.code !== 200 && evt.errorMessage) {
            setErrorMessage(evt.errorMessage);
          }
          setFileUploadComplete(evt);
        },
      }),
      [setFileProgress, setFileUploadComplete],
    );
    return (
      <div className="bq-uploader-wrapper">
        <div className="bq-uploader-header">
          <div
            {...getRootProps()}
            className="bq-uploader-dropzone"
            style={{ backgroundColor: '#eee' }}
          >
            <input {...getInputProps()} />
            <Button label={'Add Files'} />
            {isDragActive ? (
              <p>Drop the files here ...</p>
            ) : (
              <p>Drag 'n' drop some files here, or click to select files</p>
            )}
          </div>
          <div className="bq-uploader-actions">
            <Button
              label={'Start Upload'}
              disabled={
                fileAtomsList.length === 0 || queueStatus === 'uploading'
              }
              onClick={() => {
                setErrorMessage(null);
                startUploads();
              }}
            />
            <br />
            <Button
              label={'Clear Files'}
              className="mt-2"
              disabled={fileAtomsList.length === 0}
              onClick={() => {
                setErrorMessage(null);
                clearQueue();
              }}
            />
          </div>
        </div>

        {errorMessage && (
          <Message
            severity="error"
            text={errorMessage}
            className="mt-2 mb-2 w-full"
            style={{ display: 'block' }}
          />
        )}

        <div className="bq-file-upload-queue">
          {fileAtomsList.map((fileAtom, idx) => (
            <div key={idx}>
              <FileUploadItem fileAtom={fileAtom} />
            </div>
          ))}
        </div>
      </div>
    );
  },
);
